/**
 * React Query Configuration
 * 
 * Centralized staleTime and cacheTime settings
 * for optimal perceived performance
 */

// Time constants (in milliseconds)
const SECOND = 1000;
const MINUTE = 60 * SECOND;

/**
 * Query stale times by data type
 * 
 * - INSTANT: Data that changes frequently, always fresh
 * - FAST: Data that may change but brief staleness is OK
 * - STANDARD: Most app data - good balance of freshness and speed
 * - SLOW: Rarely changing data - maximize cache hits
 * - STATIC: Almost never changes - long-term cache
 */
export const STALE_TIME = {
  /** Real-time data (0ms) - notifications, messages, live updates */
  INSTANT: 0,
  
  /** Fast refresh (30s) - user status, active sessions, most app data */
  FAST: 30 * SECOND,
  
  /** Standard (30s) - aligned with global settings to prevent stale data */
  STANDARD: 30 * SECOND,
  
  /** Slow refresh (2min) - settings, preferences */
  SLOW: 2 * MINUTE,
  
  /** Static data (5min) - rarely changes, config, options */
  STATIC: 5 * MINUTE,
} as const;

/**
 * Garbage collection times
 * How long to keep data in cache after it becomes unused
 */
export const GC_TIME = {
  /** Short-lived (5min) - frequently changing data */
  SHORT: 5 * MINUTE,
  
  /** Standard (10min) - most app data */
  STANDARD: 10 * MINUTE,
  
  /** Long-lived (30min) - stable data */
  LONG: 30 * MINUTE,
} as const;

/**
 * Pre-configured query options for common patterns
 */
export const QUERY_CONFIG = {
  /** User profile and settings - stable, cache well */
  userProfile: {
    staleTime: STALE_TIME.SLOW,
    gcTime: GC_TIME.LONG,
  },
  
  /** Lists and collections - standard refresh */
  list: {
    staleTime: STALE_TIME.STANDARD,
    gcTime: GC_TIME.STANDARD,
  },
  
  /** Real-time data - always fresh */
  realtime: {
    staleTime: STALE_TIME.INSTANT,
    gcTime: GC_TIME.SHORT,
  },
  
  /** Static reference data - long cache */
  static: {
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.LONG,
  },
  
  /** Search results - brief cache */
  search: {
    staleTime: STALE_TIME.FAST,
    gcTime: GC_TIME.SHORT,
  },
} as const;
